import React, { Component } from 'react';
import PropTypes from 'prop-types';
import deepEqual from 'deep-equal';

import VideoList from '../../app/bento-components/video-list/video-list';
import {
    createVideoRequests,
    parseVideo,
    getPageCount
} from '../../app/bento-components/video-list/video-utils';
import {
	CAROUSEL_ITEMS_PER_PAGE,
	ITEMS_PER_PAGE,
	PAGINATION_TYPES
} from '../../app/bento-components/video-list/metadata';
import VideoCTA from './video-list-cta';
import { getVideos } from './ajax';
import { ALL_SEASONS } from './constants';
import {arraysEqual} from "../../app/utils";

const htmlTag = document.querySelectorAll('html')[0];

const SMALL_BREAKPOINT_PX = 767;

/*
 * The videos per page the editor chose (4 to 8), for desktop: small screens use the carousel's.
 * Components saved before the setting had this range can hold another value; it is kept in range.
 */
export const desktopItemsPerPage = ({ itemsPerPage } = {}) =>
    Number.isFinite(itemsPerPage)
        ? Math.min(Math.max(itemsPerPage, ITEMS_PER_PAGE.min), ITEMS_PER_PAGE.max)
        : ITEMS_PER_PAGE.max;

class VideoListHOCFE extends Component {
    static propTypes = {
        id: PropTypes.string.isRequired,
        columnSize: PropTypes.number.isRequired,
        linkWidget: PropTypes.node,
        data: PropTypes.object.isRequired,
        API: PropTypes.object.isRequired,
        settings: PropTypes.shape({
            selectedSeasonFilter: PropTypes.object,
            selectedShows: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string.isRequired,
                    title: PropTypes.string.isRequired
                })
            ),
            selectedSeasons: PropTypes.arrayOf(
                PropTypes.shape({
                    label: PropTypes.string.isRequired,
                    value: PropTypes.string.isRequired
                })
            ),
            itemsPerPage: PropTypes.number.isRequired,
            displayVideoTitle: PropTypes.bool.isRequired,
            displayShowTitle: PropTypes.bool.isRequired,
            displayVideoDescription: PropTypes.bool.isRequired,
            displayVideoMeta: PropTypes.bool.isRequired,
            videoType: PropTypes.string.isRequired,
            paginationType: PropTypes.string.isRequired,
            order: PropTypes.shape({
                value: PropTypes.string.isRequired,
                title: PropTypes.string.isRequired
            }),
            player: PropTypes.shape({
                autoplay: PropTypes.bool.isRequired,
                topbar: PropTypes.bool.isRequired
            }),
            selectedRegions: PropTypes.array
        }),
        callsign: PropTypes.string
    };

    constructor(props) {
        super(props);
        this.isSmallBreakpoint = window.matchMedia(`(max-width: ${SMALL_BREAKPOINT_PX}px)`).matches;
        this.state = {
            videos: [],
            pageCount: 0,
            currentVideo: {},
            overlayVideo: false,
            showVideo: false,
            currentPage: 1,
            // already the screen's, so that componentDidMount's first request is not repeated
            settings: { ...props.settings, ...this.screenSettings(props.settings) },
            isLoadMoreEnabled: false,
            selectedSeasonFilter: ALL_SEASONS,
            videoChangeAnnouncement: ''
        };

        this.init = this.init.bind(this);
        this.videoClick = this.videoClick.bind(this);
        this.videoClose = this.videoClose.bind(this);
        this.onResize = this.onResize.bind(this);
        this.setCurrentPage = this.setCurrentPage.bind(this);
        this.showLoading = this.showLoading.bind(this);
        this.getVideosCallback = this.getVideosCallback.bind(this);
        this.videoRequests = createVideoRequests();
    }

    // small screens use the carousel, desktop the editor's pagination and videos per page
    screenSettings(settings) {
        return this.isSmallBreakpoint
            ? { paginationType: PAGINATION_TYPES.CAROUSEL, itemsPerPage: CAROUSEL_ITEMS_PER_PAGE }
            : { paginationType: settings.paginationType, itemsPerPage: desktopItemsPerPage(settings) };
    }

    componentDidMount() {
        const {
            settings: { selectedShows, selectedRegions },
            settings,
            API
        } = this.props;
        let params = {
            ...settings,
            ...this.screenSettings(settings)
        };

        if (selectedRegions) {
            params.selectedRegions = selectedRegions.map(({ value }) => value).join(',');
        }

        if (selectedShows.length) {
            this.loadVideos(API, params, this.showLoading, this.init);
        }

        window.addEventListener('resize', this.onResize, false);

        if (this.isSmallBreakpoint) {
            this.setState({
                selectedSeasonFilter: ALL_SEASONS,
                videos: []
            });
        } else{
            this.setState({
                selectedSeasonFilter: ALL_SEASONS
            });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            settings: {
                selectedShows,
                seasonFilter,
                selectedSeasons,
                itemsPerPage,
                videoType,
                order,
                paginationType,
                includePassportVideos,
                selectedRegions
            },
						API
        } = this.props;

        // if something changes in selected shows, season filter toggle or
        // selected seasons, reset the frontend season filter
        if (
            (!arraysEqual(selectedShows, prevProps.settings.selectedShows) ||
                seasonFilter !== prevProps.settings.seasonFilter ||
                (selectedSeasons &&
                    !arraysEqual(prevProps.settings.selectedSeasons, selectedSeasons))) &&
            this.state.selectedSeasonFilter.value !== ALL_SEASONS.value
        ) {
            this.setState({
                selectedSeasonFilter: ALL_SEASONS
            });
        }

        // when pagination type changes from carousel to other type itemsPerPage also changes
        if (
            paginationType !== prevProps.settings.paginationType &&
            (paginationType == PAGINATION_TYPES.CAROUSEL ||
                prevProps.settings.paginationType === PAGINATION_TYPES.CAROUSEL)
        ) {
            this.setState({
                videos: [],
                currentVideo: {},
                pageCount: 0,
                overlayVideo: false,
                showVideo: false,
                currentPage: 1
            });
            return;
        }

        // the screen crossed the breakpoint (see onResize): the carousel's page size or the editor's
        const screenChanged =
            this.state.settings.paginationType !== prevState.settings.paginationType ||
            this.state.settings.itemsPerPage !== prevState.settings.itemsPerPage;

        // if something changes in getVideos filters, reset the current page
        if (
            (screenChanged ||
                !arraysEqual(selectedShows, prevProps.settings.selectedShows) ||
                !arraysEqual(selectedSeasons, prevProps.settings.selectedSeasons) ||
                seasonFilter !== prevProps.settings.seasonFilter ||
                this.state.selectedSeasonFilter !== prevState.selectedSeasonFilter ||
                itemsPerPage !== prevProps.settings.itemsPerPage ||
                videoType !== prevProps.settings.videoType ||
                !deepEqual(order, prevProps.settings.order) ||
                paginationType !== prevProps.settings.paginationType ||
                includePassportVideos !== prevProps.settings.includePassportVideos ||
                selectedRegions !== prevProps.settings.selectedRegions) &&
            this.state.currentPage !== 1
        ) {
            this.setState({ currentPage: 1 });
            return;
        }

        if (
            screenChanged ||
            !arraysEqual(selectedShows, prevProps.settings.selectedShows) ||
            !arraysEqual(selectedSeasons, prevProps.settings.selectedSeasons) ||
            seasonFilter !== prevProps.settings.seasonFilter ||
            this.state.selectedSeasonFilter !== prevState.selectedSeasonFilter ||
            itemsPerPage !== prevProps.settings.itemsPerPage ||
            this.state.currentPage !== prevState.currentPage ||
            videoType !== prevProps.settings.videoType ||
            !deepEqual(order, prevProps.settings.order) ||
            paginationType !== prevProps.settings.paginationType ||
            includePassportVideos !== prevProps.settings.includePassportVideos ||
            selectedRegions !== prevProps.settings.selectedRegions
        ) {
            // load videos if there is a selected show, all other filters have default values
            if (selectedShows.length) {
                let params = {
                    ...this.state.settings,
                    page: this.state.currentPage
                };

                if (this.state.selectedSeasonFilter && this.state.selectedSeasonFilter.value) {
                    params.seasonId = this.state.selectedSeasonFilter.value;
                    params.selectedShows = [];
                }
                // disable Load More button while loading videos
                if(this.state.isLoadMoreEnabled) {
                    if (paginationType === PAGINATION_TYPES.LOAD_MORE || paginationType === PAGINATION_TYPES.CAROUSEL) {
                        this.setState({isLoadMoreEnabled: false});
                    }
                }
                let isReset = this.state.currentPage === 1;
                this.loadVideos(API, params, () => this.showLoading(isReset), this.getVideosCallback);
            } else {
                // 	if Show filter is empty/reset, reset state
                this.setState({
                    videos: [],
                    currentVideo: {},
                    pageCount: 0,
                    overlayVideo: false,
                    showVideo: false,
                    currentPage: 1
                });
            }
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.onResize);
    }

    onResize(){
        this.isSmallBreakpoint = window.matchMedia(`(max-width: ${SMALL_BREAKPOINT_PX}px)`).matches;
        const screen = this.screenSettings(this.props.settings);
        // componentDidUpdate then asks for the first page with them
        if (
            this.state.settings.paginationType !== screen.paginationType ||
            this.state.settings.itemsPerPage !== screen.itemsPerPage
        ) {
            this.setState({
                settings: {
                    ...this.state.settings,
                    ...screen
                }
            });
        }
    }

    // a slower answer to an obsolete request (another page, season or screen size) must not replace the
    // videos (see createVideoRequests)
    loadVideos(API, params, showLoading, onLoaded) {
        return this.videoRequests.track(getVideos(API, params, showLoading), params, onLoaded);
    }

    init(response) {
        const videos = response.data.map((video) => parseVideo(video));
        this.setState((state, props) => {
            return {
                videos: videos,
                pageCount: getPageCount(response),
                currentVideo: videos[0],
                isLoadMoreEnabled: props.settings.paginationType === PAGINATION_TYPES.LOAD_MORE
            };
        });
    }

    videoClick(e, video) {
        e.preventDefault();
        const { overlayVideo } = this.state;
        this.setState({
            currentVideo: video,
            showVideo: true,
            videoChangeAnnouncement: `Now playing: ${video.videoTitle || video.title || ''}`
        });
        if (overlayVideo) {
            htmlTag.classList.add('modal-open');
        }
    }

    videoClose(e) {
        this.setState({
            showVideo: false
        });
        htmlTag.classList.remove('modal-open');
    }

    setCurrentPage = (pageNumber) => {
        let pageCount = Math.max(1, this.state.pageCount);
        const validPage = Math.min(Math.max(1, pageNumber), pageCount);
        this.setState({
            currentPage: validPage
        });
    };

    // `page` is the page the videos were asked for: the first one replaces the list even when an
    // arrow already asked for the next one (e.g. right after a season change)
    getVideosCallback = (response, page = this.state.currentPage) => {
        // the page size the videos were asked for (see showLoading)
        const {
            settings: { itemsPerPage }
        } = this.state;

        let newState = {};
        let isInit = page === 1;
        let paginationType = this.isSmallBreakpoint ? PAGINATION_TYPES.CAROUSEL : this.props.settings.paginationType;

        if (paginationType === PAGINATION_TYPES.LOAD_MORE) {
            if (isInit) {
                newState.videos = response.data.map((video) => parseVideo(video));
                newState.currentVideo = newState.videos[0];
            } else {
                let videos = this.state.videos.slice(0, -itemsPerPage);
                newState.videos = videos.concat(response.data.map((video) => parseVideo(video)));
            }
            newState.isLoadMoreEnabled = true;
        }

        if (paginationType === PAGINATION_TYPES.CAROUSEL) {
            let videos = this.state.videos;
            newState.videos = videos.concat(response.data.map((video) => parseVideo(video)));
            if (isInit) {
                newState.videos = [...response.data.map((video) => parseVideo(video))];
                newState.currentVideo = newState.videos[0];
            }
        }

        if (paginationType === PAGINATION_TYPES.PAGINATION) {
            newState.videos = [...response.data.map((video) => parseVideo(video))];
            if (isInit) {
                newState.currentVideo = newState.videos[0];
            }
        }

        this.setState({
            ...newState,
            pageCount: getPageCount(response)
        });
    };

    showLoading = (reset = false) => {
        const {
            settings: { itemsPerPage, paginationType }
        } = this.state;
        const { videos } = this.state;
        // Load More waits for the answer (getVideosCallback enables it), also for the first page of a
        // season or screen size: the page count is still the previous one's, so a click would ask for
        // its next page and that answer would trim the new first page
        const isLoadMoreEnabled = paginationType !== PAGINATION_TYPES.LOAD_MORE;
        let newVideos;

        if (paginationType === PAGINATION_TYPES.CAROUSEL) {
            return;
        }

        if (paginationType === PAGINATION_TYPES.LOAD_MORE && !reset) {
            newVideos = [...videos, ...Array(itemsPerPage).fill({})];
        } else {
            newVideos = Array(itemsPerPage).fill({});
        }

        this.setState({
            videos: newVideos,
            isLoadMoreEnabled
        });
    };

    // componentDidUpdate asks for the season's first page, with the settings of the screen size
    handleSelectSeasonFilter = (season) => {
        this.setState({
            selectedSeasonFilter: season,
            currentPage: 1
        });
    };

    render() {
        const {
            videos,
            pageCount,
            currentVideo,
            showVideo,
            overlayVideo,
            currentPage,
            isLoadMoreEnabled,
            selectedSeasonFilter,
            videoChangeAnnouncement
        } = this.state;

        const { data, id, columnSize, callsign, site } = this.props;
        const VideoListTitle = () => (
            <h1 className="video-list-title">{data.title || data.titlePlaceholder}</h1>
        );

        return (
            <div className="bento-component" role="region" aria-label="Video List Component">
                {/* Screen-reader-only live region for loading state */}
                <div
                    className="sr-only"
                    aria-live="assertive"
                    aria-atomic="true"
                    role="status">
                    {videoChangeAnnouncement
                        ? videoChangeAnnouncement
                        : videos.length === 0
                            ? 'Loading videos…'
                            : `${videos.length} video${videos.length !== 1 ? 's' : ''} loaded`}
                </div>
                <VideoList
                    {...this.props}
                    columnSize={columnSize}
                    videos={videos}
                    settings={this.state.settings}
                    data={data}
                    pageCount={pageCount}
                    currentPage={currentPage}
                    setCurrentPage={this.setCurrentPage}
                    componentId={id}
                    CallToAction={VideoCTA}
                    currentVideo={currentVideo}
                    videoClick={this.videoClick}
                    videoClose={this.videoClose}
                    VideoListTitle={VideoListTitle}
                    showVideo={showVideo}
                    playerClass={overlayVideo ? 'overlay' : ''}
                    isLoadMoreEnabled={isLoadMoreEnabled}
                    callsign={callsign}
                    currentSite={{ isPillButtonAvailable: site.is_pill_button_available }}
                    handleSelectSeasonFilter={this.handleSelectSeasonFilter}
                    selectedSeasonFilter={selectedSeasonFilter}
                />
            </div>
        );
    }
}

export default VideoListHOCFE;
